/**
 * Phone-only bar fixed to the bottom of the screen, hidden above 720px unless `always` (cockatrice-ui
 * `src/ui/BottomBar.jsx`, ported to hyperscript; PLAN.md §0).
 * Either pass `items` for 2–4 tab buttons ({ value, label, icon?, href?, badge? }) with
 * `value` + `onChange`, or pass children (for example a full-width Segmented control).
 * Respects the safe-area inset. Give the page bottom padding: var(--ui-bottom-bar-h).
 */
import './css/bottom-bar.css';
import { mergeProps, splitProps } from 'solid-js';
import Icon from './Icon.ts';
import type { IconName } from './Icon.ts';
import { component, each, el, when } from './solid.ts';
import type { Attributes, Child } from './solid.ts';
import { uiText } from './text.ts';

export interface BottomBarItem {
  /** Unique value. */
  readonly value: string;
  /** Visible label (always shown). */
  readonly label: Child;
  /** Icon name from Icon.jsx. */
  readonly icon?: IconName | undefined;
  /** Render as a link. */
  readonly href?: string | undefined;
  /** Small count. */
  readonly badge?: number | string | undefined;
}

export interface BottomBarProps extends Attributes {
  /** 2–4 items. Omit to use children. */
  readonly items?: readonly BottomBarItem[] | undefined;
  /** Selected item. */
  readonly value?: string | undefined;
  /** Called when an item is chosen. */
  readonly onChange?: ((value: string) => void) | undefined;
  /** Accessible name. Default "Views". */
  readonly label?: string | undefined;
  /** Show at every width, not only phones. */
  readonly always?: boolean | undefined;
  /** "fixed" (default) or "static" for previews. */
  readonly position?: 'fixed' | 'static' | undefined;
  /** Content when there are no items, e.g. a full-width Segmented. */
  readonly children?: Child;
  /** Extra classes. */
  readonly class?: string | undefined;
}

export default function BottomBar(props: BottomBarProps): Child {
  const [local, rest] = splitProps(props, ['class', 'items', 'value', 'onChange', 'label', 'always', 'children', 'position']);
  return el(
    'nav',
    mergeProps(
      {
        class: () =>
          ['ui-bottom-bar', local.always === true && 'ui-bottom-bar--always', local.position === 'static' && 'ui-bottom-bar--static', local.class]
            .filter(Boolean)
            .join(' '),
        'aria-label': () => local.label ?? uiText()('ui.bottom-bar.label'),
      },
      rest,
    ),
    when(
      () => local.items !== undefined,
      () =>
        el(
          'ul',
          { class: 'ui-bottom-bar__items' },
          each(
            () => local.items ?? [],
            (it) => {
              const on = (): boolean => it.value === local.value;
              const choose = (): void => {
                local.onChange?.(it.value);
              };
              const body: Child = [
                when(
                  () => it.icon !== undefined,
                  () =>
                    component(Icon, {
                      get name() {
                        return it.icon ?? 'plus';
                      },
                    }),
                ),
                el('span', { class: 'ui-bottom-bar__label' }, () => it.label),
                when(
                  () => it.badge !== undefined,
                  () => el('span', { class: 'ui-bottom-bar__badge' }, () => it.badge),
                ),
              ];
              return el(
                'li',
                {},
                when(
                  () => it.href !== undefined && it.href !== '',
                  () => el('a', { class: 'ui-bottom-bar__item', href: () => it.href, 'aria-current': () => (on() ? 'page' : undefined), onClick: choose }, body),
                  () => el('button', { type: 'button', class: 'ui-bottom-bar__item', 'aria-pressed': () => (on() ? 'true' : 'false'), onClick: choose }, body),
                ),
              );
            },
          ),
        ),
      () => el('div', { class: 'ui-bottom-bar__slot' }, () => local.children),
    ),
  );
}
